import React, { useContext } from "react";

import {
  TUser,
  IAuthProps,
  EAuthActionType,
  EAuthType,
  TAuthTypeBasicValues,
  TAuthTypeBTokenValues,
  TAuthRetrieveUserCallback,
  TAuthProps,
} from "./interfaces";
import reducer, { defaultState } from "./reducer";

import API from "@/helpers/API";
import Cookie from "@/helpers/Cookie";
import { getExpirationDate } from "@/helpers/token";
import { CampaignContext } from "./campaignInfoProvider";
import { LaboratoryContext } from "./labInfoProvider";
import { clearPharmacyStorage } from "./pharmacyInfoProvider";

const laboratoryIdentifierCookieName = "__laboratoryIdentifier";

const getLaboratoryIdentifierFromCookie = (): string | undefined =>
  Cookie.get(laboratoryIdentifierCookieName) ?? undefined;

const setLaboratoryIdentifierCookie = (
  laboratoryIdentifier: string,
  token: string,
  tokenExpirationField?: string,
  tokenTimestampShift?: number,
) => {
  const expires = getExpirationDate(
    token,
    tokenExpirationField,
    tokenTimestampShift,
  );

  if (expires) {
    Cookie.set(laboratoryIdentifierCookieName, laboratoryIdentifier, {
      expires,
    });
  } else {
    Cookie.set(laboratoryIdentifierCookieName, laboratoryIdentifier);
  }
};

export const AuthContext = React.createContext<IAuthProps>({
  user: null,
  laboratoryIdentifier: undefined,
  login: (values, user, options) => {},
  logout: () => {},
  editUser: (user: TUser) => {},
});

AuthContext.displayName = "Auth";

export const AuthConsumer = AuthContext.Consumer;

const AuthProvider: React.FC<TAuthProps> = ({
  onRetrieveUserFromCookie,
  tokenExpirationField,
  tokenTimestampShift = 1000,
  renderLoading = null,
  refreshTokenOptions,
  children,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [state, dispatch] = React.useReducer(reducer, defaultState);
  const { token: campaignToken, campaignLoading } =
    React.useContext(CampaignContext);
  const { token: labToken, labLoading } = useContext(LaboratoryContext);

  /**
   * Config Authentification context's callbacks
   */
  const authContext = React.useMemo<IAuthProps>(
    () => ({
      ...state,

      login: (
        values,
        user,
        { type = EAuthType.BToken, refreshToken, laboratoryIdentifier } = {},
      ) => {
        switch (type) {
          case EAuthType.Basic:
            const { username, password } = values as TAuthTypeBasicValues;

            API.setAuthBasic!(username, password);
            break;
          case EAuthType.BToken:
            API.setAuthToken!(
              values as TAuthTypeBTokenValues,
              tokenExpirationField,
              tokenTimestampShift,
            );
            if (refreshToken) {
              API.setRefreshToken!(refreshToken, tokenExpirationField);
            }
            if (laboratoryIdentifier) {
              setLaboratoryIdentifierCookie(
                laboratoryIdentifier,
                values as TAuthTypeBTokenValues,
                tokenExpirationField,
                tokenTimestampShift,
              );
            } else {
              Cookie.delete(laboratoryIdentifierCookieName);
            }
            break;
        }
        dispatch({ type: EAuthActionType.Set, user, laboratoryIdentifier });
      },

      logout: () => {
        API.forgetAuthToken!();
        clearPharmacyStorage();
        Cookie.delete(laboratoryIdentifierCookieName);
        dispatch({ type: EAuthActionType.Set, user: null });
      },

      editUser: (user: TUser) => {
        dispatch({ type: EAuthActionType.Edit, user });
      },
    }),
    [state, tokenExpirationField, tokenTimestampShift],
  );

  /**
   * Check if user exist before loading page.
   */

  React.useEffect(() => {
    // Try to get a token from a cookie, campaignToken or labToken
    const token = API.getTokenFromCookie!() || campaignToken || labToken;

    if (refreshTokenOptions) {
      if (!refreshTokenOptions.tokenExpirationField) {
        refreshTokenOptions.tokenExpirationField = tokenExpirationField;
      }

      API.initRefreshToken!(refreshTokenOptions);
    }

    if (token) {
      const laboratoryIdentifier = getLaboratoryIdentifierFromCookie();
      API.setAuthToken!(token, tokenExpirationField);
      onRetrieveUserFromCookie(token)
        .then((user) => {
          dispatch({ type: EAuthActionType.Set, user, laboratoryIdentifier });
        })
        .catch(() => {
          API.forgetAuth!();
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignToken, labToken]);

  /**
   * If token is valid, set loading to false once user is loaded.
   */
  React.useEffect(() => {
    if (loading === true && state.user !== null) {
      setLoading(false);
    }
  }, [loading, state.user]);

  return loading || campaignLoading || labLoading ? (
    renderLoading
  ) : (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
};

export { EAuthType };

export type { TAuthRetrieveUserCallback };

export default AuthProvider;
